// approver-onboarding-modal/index.tsx
import React, { useCallback } from 'react'
import { App } from 'antd'
import { IntlProvider } from 'react-intl'
import AppLocale from '@vacationtracker/shared/i18n/language-provider'
import { IUserInfo } from '../../types/users'
import { ILocale } from '@vacationtracker/shared/types/i18n'
import { ApproverOnboardingProvider, useApproverOnboardingContext } from './context'
import { WelcomeStep } from './steps/welcome'
import { ManualAddingStep } from './steps/manual-add'
import { ReceivingRequestsStep } from './steps/request-approval'
import { CompletionStep } from './steps/completion'
import { getApproverOnboardingProgress, saveApproverOnboardingProgress } from '../../util/approver-onboarding-storage'
import { track } from '../../services/analytics/analytics'
import './styles.less'

interface ApproverOnboardingModalProps {
  onClose?: () => void
  onExploreFirst?: () => void
  onMinimize?: () => void
  authUser: IUserInfo
  locale: ILocale
  departmentNames: string[]
  companyId: string
}

const ApproverOnboardingModalContent: React.FC<ApproverOnboardingModalProps> = ({ onExploreFirst, onMinimize }) => {
  const { currentStep, previousStep } = useApproverOnboardingContext()

  const handleClose = useCallback(() => {
    onMinimize?.()
  }, [onMinimize])

  const handleExploreFirst = useCallback(() => {
    onExploreFirst?.()
  }, [onExploreFirst])

  const handleBack = useCallback(() => {
    previousStep()
  }, [previousStep])

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep onExploreFirst={handleExploreFirst} />
      case 1:
        return <ReceivingRequestsStep onBack={handleBack} />
      case 2:
        return <ManualAddingStep onBack={handleBack} />
      case 3:
        return <CompletionStep onClose={handleClose} onBack={handleBack} />
      default:
        return <WelcomeStep onExploreFirst={handleExploreFirst} />
    }
  }

  return (
    <div className="approver-onboarding-modal">
      <div className="onboarding-content">{renderCurrentStep()}</div>
    </div>
  )
}

export const ApproverOnboardingModal: React.FC<ApproverOnboardingModalProps> = ({ onClose, onExploreFirst, onMinimize, authUser, locale, departmentNames, companyId }) => {
  const initialProgress = getApproverOnboardingProgress(authUser.id, companyId)

  return (
    <ApproverOnboardingProvider authUser={authUser} companyId={companyId} departmentNames={departmentNames} initialProgress={initialProgress || undefined}>
      <ApproverOnboardingModalContent
        onClose={onClose}
        onExploreFirst={onExploreFirst}
        onMinimize={onMinimize}
        authUser={authUser}
        locale={locale}
        departmentNames={departmentNames}
        companyId={companyId}
      />
    </ApproverOnboardingProvider>
  )
}

// Helper function to show the modal imperatively
export const showApproverOnboardingModal = (
  modal: ReturnType<typeof App.useApp>['modal'],
  authUser: IUserInfo,
  locale: ILocale,
  departmentNames: string[],
  props?: Omit<ApproverOnboardingModalProps, 'authUser' | 'locale' | 'departmentNames'>
) => {
  let modalRef: any = null

  const handleClose = () => {
    if (modalRef) {
      modalRef.destroy()
      modalRef = null
    }
    props?.onClose?.()
  }

  const handleMinimize = () => {
    if (modalRef) {
      modalRef.destroy()
      modalRef = null
    }
    props?.onMinimize?.()
  }

  const handleExploreFirst = () => {
    if (modalRef) {
      modalRef.destroy()
      modalRef = null
    }
    props?.onExploreFirst?.()
  }

  // Get the messages for the current locale
  const localeMessages = AppLocale[locale.locale]?.messages || AppLocale['en-US'].messages

  // Primary department name for storage (first one)
  const primaryDepartmentName = departmentNames[0] || 'Your Department'

  modalRef = modal.info({
    title: null,
    icon: null,
    width: 1200,
    closable: true,
    maskClosable: false,
    centered: true,
    className: 'approver-onboarding-modal-wrapper',
    content: (
      <IntlProvider locale={locale.locale} messages={localeMessages}>
        <ApproverOnboardingModal
          {...props}
          authUser={authUser}
          locale={locale}
          departmentNames={departmentNames}
          onClose={handleClose}
          onMinimize={handleMinimize}
          onExploreFirst={handleExploreFirst}
          companyId={props?.companyId || ''}
        />
      </IntlProvider>
    ),
    footer: null,
    onCancel: () => {
      // Track X button dismissal
      if (authUser && props?.companyId) {
        track('APPROVER_ONBOARDING_DISMISSED', {
          dismiss_method: 'X_BUTTON',
          company_id: props.companyId,
          user_id: authUser.id,
        })

        const currentProgress = getApproverOnboardingProgress(authUser.id, props.companyId)
        const currentStep = currentProgress?.currentStep || 0

        // If user is on completion step (step 3), mark as completed
        // Otherwise, save progress so they can resume later
        const shouldMarkComplete = currentStep === 3
        saveApproverOnboardingProgress(authUser.id, props.companyId, currentStep, primaryDepartmentName, shouldMarkComplete)
      }
      handleMinimize() // Use minimize instead of close to keep modal dismissible
    },
  })

  return modalRef
}
